<template>
  <v-card
    :border="border"
    :data-testid="cardTestId || undefined"
    :style="cardItemRootStyle"
    :tile="tile"
    :variant="variant"
    class="standard-card"
  >
    <template
      v-if="title || $slots.title"
      #title
    >
      <slot name="title">
        <div
          :class="cardTitleClass"
          :style="expandTitle && !hasTitleAction ? { minHeight: '44px' } : undefined"
          class="d-flex"
        >
          <v-row
            class="d-flex align-center"
            no-gutters
          >
            <v-col
              :cols="titleCols"
              class="pa-0"
            >
              <div class="d-flex align-center ga-3">
                <slot name="title-prepend"></slot>

                <span
                  :class="mainPageTitle ? 'fs-28 lh-40' : 'fs-22 lh-24'"
                  class="ws-pre-line"
                >
                  {{ $bct(title) }}
                </span>

                <slot name="title-append"></slot>
              </div>
            </v-col>

            <template v-if="hasTitleAction">
              <v-col
                :cols="titleActionCols"
                class="pa-0 d-flex justify-end"
              >
                <slot name="titleAction">
                  <StandardButton
                    @click="$emit('title-action-clicked')"
                    :aria-label="
                      $bct(titleAction.ariaLabel || titleAction.text || titleAction.title)
                    "
                    :color="titleActionButtonColor"
                    :custom-classes="titleAction.customClasses"
                    :data-testid="titleAction.testId"
                    :disabled-text="titleAction.disabledText"
                    :disabled-text-translate="titleAction.disabledTextTranslate"
                    :icon="titleAction.icon"
                    :loading="processing"
                    :prepend-icon="titleAction.prependIcon"
                    :prevent-double-click="titleAction.preventDoubleClick ?? true"
                    :role="titleAction.role"
                    :size="titleActionButtonSize"
                    :text="titleAction.text"
                    :text-translate="titleAction.textTranslate"
                    :to="titleAction.to"
                    :variant="titleActionButtonVariant"
                  />
                </slot>
              </v-col>
            </template>
          </v-row>
        </div>
      </slot>
    </template>

    <template
      v-if="subtitle || $slots.subtitle"
      #subtitle
    >
      <slot name="subtitle">
        <span
          v-if="subtitle"
          class="ws-pre-line"
        >
          {{ $bct(subtitle) }}
        </span>
      </slot>
    </template>

    <v-divider
      v-if="showTopDivider"
      role="presentation"
    />

    <v-card-text
      v-if="showDefaultSlot"
      :class="cardTextClass"
      :style="cardTextStyle"
    >
      <slot />
    </v-card-text>

    <v-divider v-if="showBottomDivider" />

    <slot name="actions">
      <template v-if="hasActions">
        <v-card-actions
          :class="[{ 'justify-space-between': actionsAlign === 'justify' }, cardActionsClass]"
          class="d-flex"
        >
          <v-spacer v-if="actionsAlign === 'right'" />

          <template
            v-for="action in actions"
            :key="action.title"
          >
            <StandardButton
              @click="$emit('action-clicked', { action: action.event })"
              :aria-label="$bct(action.ariaLabel || action.title)"
              :color="action.color"
              :data-testid="action.testId"
              :disabled-text="action.disabledText"
              :full-width="action.fullWidth"
              :loading="processing"
              :prepend-icon="action.prependIcon"
              :role="action.role"
              :text="$bct(action.title)"
              :variant="action.variant"
            />
          </template>
        </v-card-actions>
      </template>
    </slot>
  </v-card>
</template>

<script setup>
import { useSlots } from 'vue';

const slots = useSlots();

const $bct = inject('translator');

const props = defineProps({
  actions: {
    type: Array,
    default: () => [],
  },
  actionsAlign: {
    type: String,
    default: null,
  },
  border: {
    type: Boolean,
    default: true,
  },
  cardActionsClass: {
    type: String,
    default: null,
  },
  cardTestId: {
    type: String,
    default: null,
  },
  cardTextClass: {
    type: String,
    default: null,
  },
  cardTextStyle: {
    type: Object,
    default: null,
  },
  cardTitleClass: {
    type: String,
    default: null,
  },
  cardTitlePadding: {
    type: [String, Number],
    default: undefined,
  },
  divided: {
    type: Boolean,
    default: true,
  },
  expandTitle: {
    type: Boolean,
    default: false,
  },
  mainPageTitle: {
    type: Boolean,
    default: false,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  showDefaultSlot: {
    type: Boolean,
    default: true,
  },
  subtitle: {
    type: String,
    default: null,
  },
  tile: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: null,
  },
  titleAction: {
    type: Object,
    default: null,
  },
  titleActionCols: {
    type: [Number, String],
    default: 3,
  },
  variant: {
    type: String,
    default: 'flat',
  },
});

defineEmits(['action-clicked', 'title-action-clicked']);

const cardItemRootStyle = computed(() => {
  const padding = props.cardTitlePadding;
  if (padding === undefined || padding === null || padding === '') {
    return undefined;
  }
  const value = typeof padding === 'number' ? `${padding}px` : String(padding);
  return { '--standard-card-item-padding': value };
});

const hasTitleAction = computed(
  () =>
    Boolean(slots['titleAction']) ||
    (props.titleAction != null && Object.keys(props.titleAction).length > 0),
);

const titleCols = computed(() => (hasTitleAction.value ? 12 - Number(props.titleActionCols) : 12));

const titleActionIsIconOnly = computed(
  () => Boolean(props.titleAction?.icon) && !props.titleAction?.text,
);

const titleActionButtonColor = computed(
  () => props.titleAction?.color ?? (titleActionIsIconOnly.value ? 'grey' : null),
);

const titleActionButtonSize = computed(
  () => props.titleAction?.size ?? (titleActionIsIconOnly.value ? 'x-small' : null),
);

const titleActionButtonVariant = computed(
  () => props.titleAction?.variant ?? (titleActionIsIconOnly.value ? 'flat' : null),
);

const hasActions = computed(() => props.actions.filter((action) => action != null).length > 0);

const showBottomDivider = computed(() => {
  return props.divided && props.showDefaultSlot && (hasActions.value || slots.actions);
});

const showTopDivider = computed(() => {
  return (
    props.divided &&
    (props.title || props.subtitle || slots.title || slots.subtitle) &&
    (props.showDefaultSlot || hasActions.value || slots.actions)
  );
});
</script>

<style scoped>
.standard-card > :deep(.v-card-item) {
  padding: var(--standard-card-item-padding, 16px);
}
</style>
